import { canv } from "@/canv/global.js";
import { getViewport } from "@/canvasController/helpers";
import {
    IMAGE_FORMATS,
    MAX_IMAGE_HEIGHT,
    MAX_IMAGE_SIZE,
    MAX_IMAGE_WIDTH,
} from "@/constants";
import { CommentWidget } from "@/elements/comment";

export function isBrowser() {
    if (window !== undefined && window["forceIsBrowser"]) {
        return true;
    }

    // Check if the environment is Node.js
    if (typeof process === "object" && typeof require === "function") {
        return false;
    }

    // Check if the environment is a
    // Service worker
    if (typeof importScripts === "function") {
        return false;
    }

    // Check if the environment is a Browser
    if (typeof window === "object") {
        return true;
    }
}

export const isInIframe = window.self !== window.top;

export function createBlobFromString(message) {
    const obj = { hello: "world" };
    const blob = new Blob([message], { type: "application/json" });
    return blob;
}

export function createWidgetsDumpsBlobFromString(message) {
    const blob = new Blob([message], { type: "text/plain" });
    return blob;
}

export function randomIntFromInterval(min, max) {
    return Math.floor(Math.random() * (max - min + 1) + min);
}

export function getTarget(e, className) {
    var elem = null;
    var path = e.composedPath();

    for (var i = 0; i < path.length; i++) {
        var classesList = path[i].classList;
        if (classesList == undefined) return elem;
        if (classesList.contains(className)) {
            elem = path[i];
            break;
        }
    }
    return elem;
}

export function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

JSON.safeStringify = (obj, ignore) => {
    return JSON.stringify(obj, function replacer(key, value) {
        if (key === "parent") return undefined;
        else if (key === "parentItem") return undefined;
        else return value;
    });
};

export function lightOrDark(color) {
    if (color === "transparent") {
        return "light";
    }

    if (color === undefined) {
        return "light";
    }

    // Variables for red, green, blue values
    var r, g, b, hsp;

    // Check the format of the color, HEX or RGB?
    if (color.match(/^rgb/)) {
        // If RGB --> store the red, green, blue values in separate variables
        color = color.match(
            /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/,
        );

        r = color[1];
        g = color[2];
        b = color[3];
    } else {
        // If hex --> Convert it to RGB: http://gist.github.com/983661
        color = +("0x" + color.slice(1).replace(color.length < 5 && /./g, "$&$&"));

        r = color >> 16;
        g = (color >> 8) & 255;
        b = color & 255;
    }

    // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
    hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

    // Using the HSP value, determine whether the color is light or dark
    if (hsp > 150) {
        return "light";
    } else {
        return "dark";
    }
}

export const generateUID = () => {
    let d = new Date().getTime(),
        d2 = (performance && performance.now && performance.now() * 1000) || 0;
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        let r = Math.random() * 16;
        if (d > 0) {
            r = ((d + r) % 16) | 0;
            d = Math.floor(d / 16);
        } else {
            r = ((d2 + r) % 16) | 0;
            d2 = Math.floor(d2 / 16);
        }
        return (c == "x" ? r : (r & 0x7) | 0x8).toString(16);
    });
};

export function matrixTranslate(x, y) {
    canv.m[4] += x + canv.m[2] * y;
    canv.m[5] += canv.m[1] * x + y;
}

export function reverseTransformPoint(px, py) {
    var x = px;
    var y = py;

    px = x + canv.m[4];
    py = y + canv.m[5];

    px = px / canv.dpr;
    py = py / canv.dpr;

    return {
        x: px,
        y: py,
    };
}

/**
 *
 * @param {string} key
 * @param {object} obj
 * @param {boolean} [defaultValue=false]
 * @returns {boolean}
 */
export const getBoolProperty = (key, obj, defaultValue = false) => {
    try {
        if (obj[key] === "true") {
            return true;
        }
        if (obj[key] === "false") {
            return false;
        }
        return Boolean(obj[key]);
    } catch (e) {
        console.log(`trying to get bool by key "${key}"`, obj);
        console.error(e);
        return defaultValue;
    }
};
/**
 *
 * @param {string} key
 * @param {object} obj
 * @param {number} [defaultValue=0]
 * @returns {number}
 */
export const getNumberProperty = (key, obj, defaultValue = 0) => {
    try {
        return Number(obj[key]);
    } catch (e) {
        console.log(`trying to get number by key "${key}"`, obj);
        console.error(e);
        return defaultValue;
    }
};
/**
 *
 * @param {string} key
 * @param {object} obj
 * @param {string} [defaultValue=""]
 * @returns {string}
 */
export const getStringProperty = (key, obj, defaultValue = "") => {
    try {
        return escapeString(obj[key]);
    } catch (e) {
        console.log(`trying to get string by key "${key}"`, obj);
        console.error(e);
        return defaultValue;
    }
};

/**
 *
 * @param {string} key
 * @param {object} obj
 * @param {string} [defaultValue=""]
 * @returns {string}
 */
export const getColorProperty = (key, obj, defaultValue = "") => {
    try {
        return escapeString(obj[key]);
    } catch (e) {
        console.log(`trying to get color by key "${key}"`, obj);
        console.error(e);
        return defaultValue;
    }
};

export const normalizeImageUrl = (src) => {
    if (src.startsWith("data:image")) {
        // base64
        return src;
    }
    const [, path] = src.split("/api/");
    if (path) {
        return `${canv.host}/api/${path}`;
    }
    return src;
};

export function setClick(className, func) {
    const item = document.getElementsByClassName(className)[0];
    if (item != undefined) {
        item.addEventListener("mousedown", func, false);
    }
}

export function removeClick(className, func) {
    const item = document.querySelector(className);
    if (item) {
        item.removeEventListener("mousedown", func, false);
    }
}

export async function MakeRequest(
    handler,
    requestData,
    noResponse = false,
    niceToHave = false,
    keepAlive = false,
) {
    var result;
    try {
        const keepalive = keepAlive ? { keepalive: true } : {};
        result = await fetch(canv.host + handler, {
            ...keepalive,
            method: "POST",
            body: JSON.stringify(requestData),
        });

        if (!result.ok) {
            if (niceToHave) return undefined;

            const logData =
                `HTTP request fail [not 200 code] | from board | ` +
                canv.host +
                handler +
                ` | data: ` +
                JSON.stringify(requestData);
            logException(new Error(logData));
            throw 1;
        }
    } catch (e) {
        if (niceToHave) return undefined;

        logException(e);
        throw 1;
    }

    if (noResponse) {
        return {};
    }

    var resJson;
    try {
        resJson = await result.json();
    } catch (e) {
        if (niceToHave) return undefined;

        logException(e);
        throw 1;
    }

    return resJson;
}

export function drawRoundedRect(x, y, w, h, r) {
    if (w < 2 * r) r = w / 2;
    if (h < 2 * r) r = h / 2;
    canv.ctx.beginPath();
    canv.ctx.moveTo(x + r, y);
    canv.ctx.arcTo(x + w, y, x + w, y + h, r);
    canv.ctx.arcTo(x + w, y + h, x, y + h, r);
    canv.ctx.arcTo(x, y + h, x, y, r);
    canv.ctx.arcTo(x, y, x + w, y, r);
    canv.ctx.closePath();
}

export function isOverRect(startX, startY, width, height) {
    var pt = canv.actionManager.getActionPoint();
    var x = pt.x;
    var y = pt.y;

    if (
        x >= startX &&
        x <= startX + width &&
        y >= startY &&
        y <= startY + height
    ) {
        return true;
    }

    return false;
}

export function easeInSine(x) {
    return 1 - Math.cos((x * Math.PI) / 2);
}

export async function urlToDataURL(url, passCredential) {
    const response = await fetch(
        url,
        passCredential ? { credentials: "include" } : {},
    );
    const blob = await response.blob();
    return new Promise((onSuccess, onError) => {
        try {
            const reader = new FileReader();
            reader.onload = function () {
                onSuccess(this.result);
            };
            reader.readAsDataURL(blob);
        } catch (e) {
            onError(e);
        }
    });
}

export const imageToDataUrl = (imageElement) => {
    const canvas = document.createElement("canvas");
    canvas.width = imageElement.naturalWidth;
    canvas.height = imageElement.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(imageElement, 0, 0);
    return canvas.toDataURL();
};

export function dataURLToBlob(dataUri) {
    var binary = atob(dataUri.split(",")[1]);
    var mimeString = dataUri.split(",")[0].split(":")[1].split(";")[0];
    var array = [];
    for (var i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type: mimeString });
}

export const isImageSizeValid = (imageSize) => imageSize <= MAX_IMAGE_SIZE;
export const isImageResolutionValid = (width, height) =>
    width * height <= MAX_IMAGE_WIDTH * MAX_IMAGE_HEIGHT;
export const isImageFormatValid = (imageFormat) =>
    IMAGE_FORMATS.includes(imageFormat);

// TODO: после переписывания здесь на ts заменить isIterable
// по этому пути /src/app/canvasController/helpers/image.ts
export const isIterable = (v) => typeof v[Symbol.iterator] === "function";

/**
 * @param {Error} error
 */
export function logException(error) {
    canv.captureException(error);
    console.log(error);
    canv.appInterface.reloadWindow.show();
}

const ESCAPE_MAP = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
    // '/': '&#x2F;',
};

const ESCAPE_REGEX = /[&<>"']/g;
const EVENT_HANDLERS_REGEX =
    /\b(vbscript|data:text\/html|javascript|onclick|onload|onerror|ondblclick|onmousedown|onmousemove|onmouseover|onmouseout|onkeypress|onkeydown|onkeyup|onsubmit|onreset|onfocus|onblur|onchange|onselect|oncut|oncopy|onpaste|oncontextmenu|onwheel|ontouchstart|ontouchend|ontouchmove|ongesturestart|ongesturechange|ongestureend)(?=\s*=|)/gi;

export const escapeString = (str) => {
    if (typeof str !== "string") return str;

    let safeStr = str.replace(ESCAPE_REGEX, (c) => ESCAPE_MAP[c]);
    safeStr = safeStr.replace(EVENT_HANDLERS_REGEX, "");

    return safeStr;
};

export const unescapeString = (str) => {
    if (typeof str !== "string") return str;

    return str.replace("&amp;", "&");
}

// для прямых линий, раньше использовалось в arrow, сейчас нет

// export function isOverLine(firstPointX, firstPointY, lastPointX, lastPointY) {
//     var allowGap = canv.actionManager.getZoomConstant(10);
//     var pt = canv.actionManager.getActionPoint();
//     var x = pt.x * canv.m[0];
//     var y = pt.y * canv.m[0];

//     var realFirstPointX = firstPointX * canv.m[0];
//     var realFirstPointY = firstPointY  * canv.m[3];
//     var realLastPointX = lastPointX * canv.m[0];
//     var realLastPointY = lastPointY * canv.m[3];
//     var realLineWidth = this.lineWidth *  canv.m[3] + allowGap;

//     if (x >= realFirstPointX && x <= realLastPointX || x >= realLastPointX && x <= realFirstPointX) {

//         // Считаем уравнение прямой
//         var k1 = realLastPointY - realFirstPointY;
//         var k2 = realLastPointX - realFirstPointX;
//         var c = k2 * realFirstPointY - k1 * realFirstPointX;

//         // Считаем y прямой для данного x
//         var calcedPointY = (k1 * x + c) / k2;

//         //  Считаем вертикальную погрешность с учетом толщины линии
//         var radiansA = Math.atan(this.width / this.height);
//         var addHeight = realLineWidth / ( 2 * Math.sin(radiansA) );

//         // Сравниваем данный y с рассчитаным y и с учетом толщины линии
//         if (calcedPointY - addHeight <= y && y <= calcedPointY + addHeight) {
//             return true;
//         }
//     }

//     return false;
// }

// // не используется
// export function isLineUnderSelecting(cursorT, firstPointX, firstPointY, lastPointX, lastPointY) {
//     var x1 = cursorT.simulatedSelectingStartX;
//     var y1 = cursorT.simulatedSelectingStartY;
//     var x2 = cursorT.simulatedSelectingStartX + cursorT.simulatedSelectingWidth;
//     var y2 = cursorT.simulatedSelectingStartY + cursorT.simulatedSelectingHeight;

//         // Считаем уравнение прямой
//         var k1 = lastPointY - firstPointY;
//         var k2 = firstPointX - lastPointX;
//         var c = firstPointY * lastPointX - firstPointX * lastPointY;

//         if (cursorT.simulatedSelectingStartX <= this.baseStartX + this.baseWidth && cursorT.simulatedSelectingStartY <= this.baseStartY + this.baseHeight && cursorT.simulatedSelectingStartX + cursorT.simulatedSelectingWidth >= this.baseStartX && cursorT.simulatedSelectingStartY + cursorT.simulatedSelectingHeight >= this.baseStartY) {
//             if ((k1*x1 + k2*y1 + c)*(k1*x2 + k2*y2 + c) < 0) {
//                 return true;
//             }
//         }

//     return false;
// }

// Функции чтобы рисовать arrowEnd для arrow (что именно считают не знаю)

export function getCubicBezierXYatT(startPt, controlPt1, controlPt2, endPt, T) {
    var x = CubicN(T, startPt.x, controlPt1.x, controlPt2.x, endPt.x);
    var y = CubicN(T, startPt.y, controlPt1.y, controlPt2.y, endPt.y);
    return { x: x, y: y };
}

// cubic helper formula at T distance
export function CubicN(T, a, b, c, d) {
    var t2 = T * T;
    var t3 = t2 * T;
    return (
        a +
        (-a * 3 + T * (3 * a - a * T)) * T +
        (3 * b + T * (-6 * b + b * 3 * T)) * T +
        (c * 3 - c * 3 * T) * t2 +
        d * t3
    );
}

export function easeOutCubic(x) {
    return 1 - Math.pow(1 - x, 3);
}

export function isSeveralEntityTypesInSelected(selected) {
    var prevCodeName = "";

    for (var i = 0; i < selected.length; i++) {
        var currentCodeName = "";

        if (selected[i].isItemWrapper()) {
            currentCodeName = selected[i].onlyChild().constructorName;
        } else {
            currentCodeName = "group";
        }

        if (prevCodeName !== "" && prevCodeName !== currentCodeName) {
            return true;
        } else {
            prevCodeName = currentCodeName;
        }
    }
}

export function debounce(func, delay) {
    let timeoutId;
    return function (...args) {

        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            func.apply(this, args);
        }, delay);
    };
}

/**
 *
 * @param {number} x
 * @param {number} y
 * @param {object} widget
 * @returns {boolean}
 */
export function isOnScreenAfterTranslate(x = 0, y = 0, widget) {
    const viewport = getViewport();

    return (
        viewport["viewportStartX"] <= widget.startX + widget.width + x &&
        viewport["viewportStartY"] <= widget.startY + widget.height + y &&
        viewport["viewportStartX"] + viewport["viewportWidth"] >=
        widget.startX + x &&
        viewport["viewportStartY"] + viewport["viewportHeight"] >= widget.startY + y
    );
}

export function isOnScreen(widget) {
    const viewport = getViewport();

    return (
        viewport["viewportStartX"] <= widget.startX + widget.width &&
        viewport["viewportStartY"] <= widget.startY + widget.height &&
        viewport["viewportStartX"] + viewport["viewportWidth"] >=
        widget.startX &&
        viewport["viewportStartY"] + viewport["viewportHeight"] >= widget.startY
    );
}

export function updateCommentsMessages(commentsActions, isApplied) {
    const commentsByUid = canv.canvasController.items.reduce((acc, cur) => {
        const isCommentWidget =
            cur.isItemWrapper() && cur.children[0] instanceof CommentWidget;
        if (isCommentWidget) {
            acc[cur.children[0].UID] = cur.children[0];
        }

        return acc;
    }, {});

    commentsActions.forEach((action) => {
        const comment = commentsByUid[action.UID];

        if (action.type === "propertyBuild" && action.action === "add") {
            if (isApplied && comment) {
                comment.addMessage(action.property);
            }
        }

        if (action.type === "propertyBuild" && action.action === "remove") {
            if (isApplied && comment) {
                comment.removeMessage(action.property.UID);
            }
        }
    });
}

function getSanitizedComment(comment) {
    return {
        entityType: getStringProperty("entityType", comment),
        UID: getStringProperty("UID", comment),
        parentUID: getStringProperty("parentUID", comment),
        startX: getNumberProperty("startX", comment),
        startY: getNumberProperty("startY", comment),
        isResolved: getBoolProperty("isResolved", comment),
        accountName: getStringProperty("accountName", comment),
        createdAt: getStringProperty("createdAt", comment),
        text: getStringProperty("text", comment),
        token: getStringProperty("token", comment),
        messages: Array.isArray(comment.messages)
            ? comment.messages.map((message) => ({
                UID: getStringProperty("UID", message),
                createdAt: getStringProperty("createdAt", message),
                token: getStringProperty("token", message),
                accountName: getStringProperty("accountName", message),
                text: getStringProperty("text", message),
            }))
            : [],
        updatedAt: getStringProperty("updatedAt", comment),
    };
}

function getSanitizedGroup(group) {
    return {
        UID: getStringProperty("UID", group),
        parentUID: getStringProperty("parentUID", group),
        itemWrapper: getBoolProperty("itemWrapper", group),
        entityType: getStringProperty("entityType", group),
        indexByParent: getStringProperty("indexByParent", group),
        arrows: group.arrows.map((arrow) => ({
            point: getStringProperty("point", arrow),
            arrowItemUID: getStringProperty("arrowItemUID", arrow),
            place: getStringProperty("place", arrow),
            share: getNumberProperty("share", arrow, 0.5),
        })),
        angle: getNumberProperty("angle", group),
        isLocked: getBoolProperty("isLocked", group),
    };
}

function getSanitizedMessage(message) {
    return {
        UID: getStringProperty("UID", message),
        createdAt: getStringProperty("createdAt", message),
        token: getStringProperty("token", message),
        accountName: getStringProperty("accountName", message),
        text: getStringProperty("text", message),
    };
}

function getSanitizedEventInfo(event) {
    const type = getStringProperty("type", event);

    if (type === "build") {
        return { type, action: getStringProperty("action", event) };
    }

    if (type === "property") {
        return {
            type,
            UID: getStringProperty("UID", event),
            fixType: getStringProperty("fixType", event),
        };
    }

    if (type === "propertyBuild") {
        return {
            type,
            action: getStringProperty("action", event),
            UID: getStringProperty("UID", event),
            fixType: getStringProperty("fixType", event),
        };
    }
}

function getValueAtPath(obj, path) {
    return path.reduce((acc, key) => acc?.[key], obj);
}

function walkDeepSanitize(input) {
    const root = input;
    const isRootArray = Array.isArray(input);
    const output = isRootArray ? [] : {};

    const stack = [
        {
            value: input,
            path: [],
            parent: null,
            keyInParent: null,
            container: output,
        },
    ];

    while (stack.length > 0) {
        const { value, path, parent, keyInParent, container } = stack.pop();

        if (Array.isArray(value)) {
            const arr = [];
            if (parent && keyInParent !== null) parent[keyInParent] = arr;

            value.forEach((item, index) => {
                stack.push({
                    value: item,
                    path: [...path, String(index)],
                    parent: arr,
                    keyInParent: index,
                    container: arr,
                });
            });

            if (!parent && path.length === 0) {
                value.forEach((_, i) => (output[i] = undefined));
                value.forEach((item, index) => {
                    stack.push({
                        value: item,
                        path: [String(index)],
                        parent: output,
                        keyInParent: index,
                        container: output,
                    });
                });
            }
        } else if (typeof value === "object" && value !== null) {
            const obj = {};
            if (parent && keyInParent !== null) parent[keyInParent] = obj;

            Object.entries(value).forEach(([key, val]) => {
                stack.push({
                    value: val,
                    path: [...path, key],
                    parent: obj,
                    keyInParent: key,
                    container: obj,
                });
            });

            if (!parent && path.length === 0) {
                Object.assign(output, obj);
                Object.entries(value).forEach(([key, val]) => {
                    stack.push({
                        value: val,
                        path: [key],
                        parent: output,
                        keyInParent: key,
                        container: output,
                    });
                });
            }
        } else {
            const parentObj = getValueAtPath(root, path.slice(0, -1));
            const key = path[path.length - 1];
            let result;

            if (typeof value === "boolean") {
                result = getBoolProperty(key, parentObj);
            } else if (typeof value === "string") {
                result = getStringProperty(key, parentObj);
            } else if (typeof value === "number") {
                result = getNumberProperty(key, parentObj);
            }

            if (parent && keyInParent !== null) {
                parent[keyInParent] = result;
            } else if (path.length === 1) {
                output[key] = result;
            }
        }
    }

    return output;
}

export function returnSanitizedCommentActionEvents(eventsData) {
    const commentsActions = [];
    eventsData.forEach((event) => {
        if (event.type === "build") {
            const comment = event?.entities?.find(
                (entity) => entity.entityType === "comment",
            );
            const group = event?.entities?.find(
                (entity) => entity.entityType === "group",
            );
            if (comment) {
                const sanitizedEvent = getSanitizedEventInfo(event);
                sanitizedEvent.comment = getSanitizedComment(comment);
                sanitizedEvent.group = getSanitizedGroup(group);

                commentsActions.push(sanitizedEvent);
            }
        }
        if (event.type === "property" && event.state.entityType === "comment") {
            const sanitizedEvent = getSanitizedEventInfo(event);
            sanitizedEvent.state = getSanitizedComment(event.state);

            commentsActions.push(sanitizedEvent);
        }
        if (event.type === "propertyBuild" && event.fixType === "messages") {
            const sanitizedEvent = getSanitizedEventInfo(event);
            sanitizedEvent.property = getSanitizedMessage(event.property);

            commentsActions.push(sanitizedEvent);
        }
    });

    return commentsActions;
}

export function returnSanitizedVoteActionEvents(eventsData) {
    const voteActions = [];
    eventsData.forEach((event) => {
        if (event.type === "build") {
            const vote = event?.entities?.find(
                (entity) => entity.entityType === "vote",
            );
            if (vote) {
                const sanitizedEvent = getSanitizedEventInfo(event);

                sanitizedEvent.vote = walkDeepSanitize(vote);

                voteActions.push(sanitizedEvent);
            }
        }
    });
    return voteActions;
}

export function isVoteCreateEvent(event) {
    if (event.type === "build" && event.action === 'create') {
        return event.entities[0].entityType === "vote";
    }

    return false;
}

export function isVoteDeleteEvent(event) {
    if (event.type === "build" && event.action === 'delete') {
        return event.entities[0].entityType === "vote";
    }

    return false;
}

export function isVoteEndedEvent(event) {
    if (event.type === "property" && event.state.isResolved) {
        return canv.canvasController.activeVote?.UID === event.UID;
    }

    return false;
}

export function isOnlyCommentWidget() {
    return (
        canv.canvasController.selected.length === 1 &&
        canv.canvasController.selected[0].isItemWrapper() &&
        canv.canvasController.selected[0].children[0] instanceof CommentWidget
    );
}

export const checkMaxNum = (num) => {
    return num > Number.MAX_SAFE_INTEGER;
};

export const commentResolveIsDisabled = () => {
    if (canv.isGuest()) {
        return true;
    }

    return !canv.isBoardModeEditOrOwner() && !canv.isBoardModeEditMini();
};

const blobToDataUrl = (b) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(b);
    });
};

export const prepareImage = async (blob) => {
    return new Promise(async (resolve) => {
        const img = new Image();
        const objectURL = await blobToDataUrl(blob);

        img.src = objectURL;
        img.crossOrigin = "anonymous";
        img.onload = () => {
            resolve(img);
        };
        img.onerror = () => {
            resolve();
        };
    });
};

export const DEFAULT_BLACK_COLOR = '#2C2D32'
export const DEFAULT_WHITE_COLOR = '#FAFAFA'

export const getContrastColor = (hexcolor) => {
    if (hexcolor.startsWith('#')) {
        hexcolor = hexcolor.slice(1);
    }
    if (hexcolor.length !== 6) {
        console.log("wrong hex color", hexcolor);
        return DEFAULT_BLACK_COLOR;
    }

    const r = parseInt(hexcolor.substring(0, 2), 16);
    const g = parseInt(hexcolor.substring(2, 4), 16);
    const b = parseInt(hexcolor.substring(4, 6), 16);

    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

    return (yiq >= 128) ? DEFAULT_BLACK_COLOR : DEFAULT_WHITE_COLOR;
};
